import { useCallback, useEffect, useState } from 'react';
import type { Category, Recipe } from '@ricettario/shared';
import { api, ApiError } from '../api/client';

type State<T> = { data: T | null; loading: boolean; error: string | null };

export function useRecipes(search: string, category: Category | null) {
  const [state, setState] = useState<State<Recipe[]>>({ data: null, loading: true, error: null });

  const fetchList = useCallback(async () => {
    setState((s) => ({ ...s, loading: true, error: null }));
    const params = new URLSearchParams();
    if (search.trim()) params.set('search', search.trim());
    if (category) params.set('category', category);
    const qs = params.toString();
    try {
      const data = await api.get<Recipe[]>(`/api/v1/recipes${qs ? `?${qs}` : ''}`);
      setState({ data, loading: false, error: null });
    } catch (err) {
      setState({
        data: null,
        loading: false,
        error: err instanceof ApiError ? err.message : 'errore',
      });
    }
  }, [search, category]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  return { ...state, refresh: fetchList };
}

export function useRecipe(id: string | undefined) {
  const [state, setState] = useState<State<Recipe>>({ data: null, loading: true, error: null });

  const refresh = useCallback(async () => {
    if (!id) return;
    setState((s) => ({ ...s, loading: true, error: null }));
    try {
      const data = await api.get<Recipe>(`/api/v1/recipes/${id}`);
      setState({ data, loading: false, error: null });
    } catch (err) {
      setState({
        data: null,
        loading: false,
        error: err instanceof ApiError ? err.message : 'errore',
      });
    }
  }, [id]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return { ...state, refresh };
}

export function useTags() {
  const [tags, setTags] = useState<string[]>([]);
  useEffect(() => {
    api.get<string[]>('/api/v1/tags').then(setTags).catch(() => setTags([]));
  }, []);
  return tags;
}
